import { ModalGeneric } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import './SupportingDocument.scss';
import { useEffect, useMemo, useState } from 'react';

import { getlocationsMonthly } from '../../../services/monthlyServiceRequest';
import {
  deleteSupportingDocument,
  deleteSupportingDocumentMultiple,
  postSupportingDocument,
  postSupportingDocumentMultiple,
  verifySupportingDocument,
  verifySupportingDocumentMultiple
} from '../../../services/workReport';
import useToast from '../../../hooks/useToast';

import SupportingDocumentForm from './SupportingDocumentForm';

const SupportingDocumentModal = ({ show, onHide, ...props }) => {
  const { data, monthly, isMultiple, updateLineData, selectedDate, maxDate, minDate } = props;
  const { setNewToast } = useToast();
  const [validateData, setValidateData] = useState({
    tienePartesNotificados: true,
    existeDocumentoRespaldo: false,
    mensaje: null
  });

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const onExit = () => {
    onHide();
  };

  useEffect(() => {
    const getlocations = async () => {
      try {
        const locationsResponse = await getlocationsMonthly(data?.idSolicitud, {});

        setLocations(
          locationsResponse?.solicitudServicioMensualizadaUbicaciones.map((location) => ({
            ...location,
            value: location,
            label: location?.nombre
          }))
        );
      } catch (error) {
        setLocations([]);
      }
    };

    if (data?.idSolicitud) {
      getlocations();
    }
  }, [data?.idSolicitud]);

  const selectedLocation = useMemo(() => {
    if (data?.ubicacionStr) {
      return {
        nombre: data?.ubicacionStr
      };
    }

    return locations?.find((location) => location?.id === data?.locationId);
  }, [locations, data]);

  const onSave = (values) => {
    if (loading) {
      return;
    }
    if (values) {
      setLoading(true);
      const commonValues = {
        fechaIncio: values?.dateFrom,
        fechaFin: values?.dateTo,
        identificador: values?.identifier
      };

      //
      if (isMultiple) {
        const body = {
          ...commonValues,
          idParteTrabajo: data?.idPartetrabajo,
          IdUbicaciones: values?.locations?.map((location) => location?.id)
        };

        postSupportingDocumentMultiple(body)
          .then((response) => {
            setNewToast({
              type: 'SUCCESS',
              message: response
            });
            updateLineData?.();
            onHide();
          })
          .catch((error) => {
            setNewToast({
              type: 'ERROR',
              message: error.message || error
            });
          });
      } else {
        const body = {
          ...commonValues,
          idUbicacionLinea: data?.idUbicacionLinea,
          idParteLinea: data?.idParteLinea
        };

        postSupportingDocument(body)
          .then((response) => {
            setNewToast({
              type: 'SUCCESS',
              message: response
            });
            updateLineData?.();
            onHide();
          })
          .catch((error) => {
            setNewToast({
              type: 'ERROR',
              message: error.message || error
            });
          });
      }
    }
  };

  const onValidate = (values) => {
    if (values) {
      const commonValues = {
        fechaIncio: values?.dateFrom,
        fechaFin: values?.dateTo,
        identificador: values?.identifier
      };

      if (isMultiple) {
        const body = {
          ...commonValues,
          idParteTrabajo: data?.idPartetrabajo,
          IdUbicaciones: values?.locations?.map((location) => location?.id) || []
        };

        if (monthly && !values?.locations?.length) {
          setValidateData({
            tienePartesNotificados: false,
            existeDocumentoRespaldo: false,
            mensaje: 'Debe seleccionar al menos una ubicación'
          });

          return;
        }

        verifySupportingDocumentMultiple(body)
          .then((response) => {
            setValidateData(response);
          })
          .catch(() => {
            setValidateData({
              tienePartesNotificados: false,
              existeDocumentoRespaldo: false,
              mensaje: 'Ocurrio un error al validar la información ingresada'
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const body = {
          ...commonValues,
          idUbicacionLinea: data?.idUbicacionLinea,
          idParteLinea: data?.idParteLinea
        };

        verifySupportingDocument(body)
          .then((response) => {
            setValidateData(response);
          })
          .catch(() => {
            setValidateData({
              tienePartesNotificados: false,
              existeDocumentoRespaldo: false,
              mensaje: 'Ocurrio un error al validar la información ingresada'
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  };

  const onDelete = (values) => {
    if (loading) {
      return;
    }
    if (values) {
      setLoading(true);
      const commonValues = {
        fechaIncio: values?.dateFrom,
        fechaFin: values?.dateTo,
        identificador: values?.identifier
      };

      if (isMultiple) {
        const body = {
          ...commonValues,
          idParteTrabajo: data?.idPartetrabajo,
          IdUbicaciones: values?.locations?.map((location) => location?.id)
        };

        deleteSupportingDocumentMultiple(body)
          .then((response) => {
            setNewToast({
              type: 'SUCCESS',
              message: response
            });
            updateLineData?.();
            onHide();
          })
          .catch((error) => {
            setNewToast({
              type: 'ERROR',
              message: error.message || error
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const body = {
          ...commonValues,
          idUbicacionLinea: data?.idUbicacionLinea,
          idParteLinea: data?.idParteLinea
        };

        deleteSupportingDocument(body)
          .then((response) => {
            setNewToast({
              type: 'SUCCESS',
              message: response
            });
            updateLineData?.();
            onHide();
          })
          .catch((error) => {
            setNewToast({
              type: 'ERROR',
              message: error.message || error
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  };

  return (
    <ModalGeneric
      backdrop
      infoModal
      loading={loading}
      onHide={onExit}
      show={show}
      size="xl"
      title="Identificador de Documento de Respaldo"
    >
      <SupportingDocumentForm
        data={data}
        isMultiple={isMultiple}
        loading={loading}
        locationOptions={locations}
        maxDate={maxDate}
        minDate={minDate}
        monthly={monthly}
        onCancel={onExit}
        onDelete={onDelete}
        onSave={onSave}
        onValidate={onValidate}
        selectedDate={selectedDate}
        selectedLocation={selectedLocation}
        validateData={validateData}
      />
    </ModalGeneric>
  );
};

SupportingDocumentModal.displayName = 'ProgramPointModalFormik';
SupportingDocumentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  data: PropTypes.shape({
    idSolicitud: PropTypes.number,
    idUbicacionLinea: PropTypes.string,
    idPartetrabajo: PropTypes.string,
    locationId: PropTypes.string,
    idParteLinea: PropTypes.number,
    ubicacionStr: PropTypes.string
  }),
  selectedDate: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  monthly: PropTypes.bool,
  isMultiple: PropTypes.bool,
  updateLineData: PropTypes.func
};

SupportingDocumentModal.defaultProps = {
  data: {},
  monthly: false,
  isMultiple: false,
  selectedDate: null,
  maxDate: null,
  minDate: null,
  updateLineData: () => {}
};
export default SupportingDocumentModal;
